import React, { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';
import './astyles/AdminDashboard.css';

interface Review {
  id: number;
  volunteer_name: string;
  review_content: string;
  rating: number;
  created_at: string;
  is_featured?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const ReviewsSection: React.FC<{ onPost?: () => void }> = ({ onPost }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [newReview, setNewReview] = useState({ review_content: '', rating: 5 });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/reviews`);
      const data = response.data as Review[];
      // Display only the most recent 3 reviews
      setReviews(data.slice(0, 3));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
      setLoading(false);
    }
  };

  const handlePostReview = async () => {
    if (!newReview.review_content.trim()) {
      alert('Please enter a review');
      return;
    }

    try {
      const volunteerId = localStorage.getItem('volunteerId') || '1';
      await axios.post(`${API_BASE_URL}/api/reviews`, {
        volunteer_name: 'Admin',
        review_content: newReview.review_content,
        rating: newReview.rating,
        created_at: new Date().toISOString()
      });

      setNewReview({ review_content: '', rating: 5 });
      setShowPostForm(false);
      fetchReviews();
      if (onPost) onPost();
    } catch (error) {
      console.error('Error posting review:', error);
      alert('Failed to post review');
    }
  };

  const getAvatarUrl = (index: number) => {
    const genders = index % 2 === 0 ? 'women' : 'men';
    return `https://randomuser.me/api/portraits/${genders}/${index % 100}.jpg`;
  };

  return (
    <div className="widget-card discussions-widget">
      <div className="widget-header-with-icon">
        <MessageSquare size={20} />
        <h3 className="widget-title">Community Reviews</h3>
      </div>

      {loading ? (
        <div className="discussions-placeholder-redesigned">
          <div className="placeholder-icon">
            <MessageSquare size={48} strokeWidth={1.5} />
          </div>
          <p className="placeholder-text">Loading reviews...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="discussions-placeholder-redesigned">
          <div className="placeholder-icon">
            <MessageSquare size={48} strokeWidth={1.5} />
          </div>
          <p className="placeholder-text">No reviews yet</p>
          <p className="placeholder-subtext">Be the first to share your experience</p>
        </div>
      ) : (
        <div className="reviews-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {reviews.map((review, index) => (
            <div key={review.id} className="review-card-mini">
              <div className="review-author-mini">
                <img 
                  src={getAvatarUrl(index)} 
                  alt={review.volunteer_name} 
                  className="review-avatar-mini"
                />
                <div className="review-info-mini">
                  <h4 className="author-name-mini">{review.volunteer_name}</h4>
                  <div className="review-rating">
                    {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                  </div>
                </div>
              </div>
              <p className="review-content-mini">{review.review_content}</p>
              <span className="review-date-mini">
                {new Date(review.created_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {!showPostForm ? (
        <button 
          className="post-review-btn"
          onClick={() => setShowPostForm(true)}
        >
          Post Review
        </button>
      ) : (
        <div className="post-review-form">
          <h4>Share Your Experience</h4>
          <textarea
            className="review-textarea"
            placeholder="Write your review..."
            value={newReview.review_content}
            onChange={(e) => setNewReview({ ...newReview, review_content: e.target.value })}
            rows={3}
          />
          <div className="rating-selector">
            <label>Rating:</label>
            <select 
              value={newReview.rating} 
              onChange={(e) => setNewReview({ ...newReview, rating: parseInt(e.target.value) })}
            >
              <option value={5}>5 Stars</option>
              <option value={4}>4 Stars</option>
              <option value={3}>3 Stars</option>
              <option value={2}>2 Stars</option>
              <option value={1}>1 Star</option>
            </select>
          </div>
          <div className="review-form-actions">
            <button className="submit-review-btn" onClick={handlePostReview}>
              Submit
            </button>
            <button className="cancel-review-btn" onClick={() => {
              setShowPostForm(false);
              setNewReview({ review_content: '', rating: 5 });
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
